import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '../../auth';
import type { Room } from '@modelcast/types';
import { EventsSection } from './EventsSection';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';

const HERO_SLIDES = [
  { img: '/images/hero/AndFillItWithArt.webp', position: 'center 40%', model: 'Emily', artist: 'Raúl Allén' },
  { img: '/images/hero/raul-andrea.webp', position: 'center center', model: 'Andrea', artist: 'Raúl Allén' },
];

export function HomePage() {
  const navigate = useNavigate();
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [myEvents, setMyEvents] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const [nextSessionAt, setNextSessionAt] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState(0);
  const [navOnDark, setNavOnDark] = useState(true);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isSignedIn) {
      fetchMyEvents();
    }
    fetchLiveCount();
    fetchNextSession();
  }, [isSignedIn]);

  // Hero slideshow with soft wipe transition
  useEffect(() => {
    if (HERO_SLIDES.length <= 1) return;
    const interval = setInterval(() => {
      setPreviousSlide(currentSlide);
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 10000); // 10 seconds per slide
    return () => clearInterval(interval);
  }, [currentSlide]);

  // Nav scroll state
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const threshold = heroRef.current.offsetHeight - 80;
        setNavOnDark(window.scrollY < threshold);
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchLiveCount = async () => {
    try {
      const response = await fetch('/api/rooms/live-count');
      if (response.ok) {
        const data = await response.json();
        setLiveCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching live count:', error);
    }
  };

  const fetchNextSession = async () => {
    try {
      const response = await fetch('/api/rooms/public');
      if (!response.ok) return;
      const data = await response.json();
      const now = Date.now();
      const upcoming = (data.rooms || [])
        .filter((room: { scheduledAt: string | null; startedAt: string | null }) =>
          !room.startedAt && room.scheduledAt && new Date(room.scheduledAt).getTime() > now
        )
        .sort((a: { scheduledAt: string }, b: { scheduledAt: string }) =>
          new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
        );
      setNextSessionAt(upcoming[0]?.scheduledAt ?? null);
    } catch (error) {
      console.error('Error fetching next session:', error);
    }
  };

  const nextSessionLabel = nextSessionAt
    ? `Next session ${new Date(nextSessionAt).toLocaleString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}`
    : null;

  const fetchMyEvents = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch('/api/rooms/my-events', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setMyEvents(data.rooms);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentCredit = HERO_SLIDES[currentSlide];

  return (
    <div className="min-h-screen flex flex-col relative bg-paper">
      {/* Nav */}
      <NavBar variant={navOnDark ? 'dark' : 'light'} />

      {/* Hero */}
      <header ref={heroRef} className="hero">
        <div className="hero-bg" aria-hidden="true">
          {/* Previous slide - stays visible underneath */}
          <div
            className="hero-bg-slide active"
            style={{
              backgroundImage: `url(${HERO_SLIDES[previousSlide].img})`,
              backgroundPosition: HERO_SLIDES[previousSlide].position
            }}
          />
          {/* Current slide - wipes in on top */}
          <div
            key={currentSlide}
            className="hero-bg-slide hero-wipe active"
            style={{
              backgroundImage: `url(${HERO_SLIDES[currentSlide].img})`,
              backgroundPosition: HERO_SLIDES[currentSlide].position,
              zIndex: 1
            }}
          />
        </div>

        <div className="hero-content">
          <div className="max-w-[1280px] mx-auto px-8">
            <div className="hero-text">
              {(liveCount > 0 || nextSessionLabel) && (
                <span className="eyebrow">
                  <span className="live-dot"></span>
                  {liveCount > 0 ? `${liveCount} sessions live now` : nextSessionLabel}
                </span>
              )}
              <h1>Life drawing, from wherever you are.</h1>
              <p className="hero-sub">
                Live, timed figure-drawing sessions with real models and artists.
              </p>
              <div className="hero-cta">
                <a href="/events" className="btn btn-lg btn-invert">
                  Browse sessions
                </a>
              </div>
            </div>
          </div>
        </div>

        <figure className="hero-credit" aria-live="polite">
          {HERO_SLIDES.length > 1 && (
            <div className="hc-dots">
              {HERO_SLIDES.map((_, index) => (
                <span key={index} className={index === currentSlide ? 'on' : ''} />
              ))}
            </div>
          )}
          <div className="hc-people">
            <div><span className="role">Artist</span><strong>{currentCredit.artist}</strong></div>
            <div><span className="role">Model</span><strong>{currentCredit.model}</strong></div>
          </div>
        </figure>
      </header>

      {/* Public Events Section */}
      <EventsSection />

      <SharedFooter />
    </div>
  );
}
